import type { PerformanceTargetComparison, UiBudgetLane, UiBudgetPoint } from '@nightshift/ui-types';

export const LANE_STATUS_LABEL: Record<UiBudgetLane['status'], string> = {
  ok: 'holding',
  regression: 'regression',
  empty: 'no samples',
  'no-baseline': 'no baseline',
};

const COMPARISON_LABEL: Record<PerformanceTargetComparison, string> = {
  lt: 'less than',
  lte: 'at most',
  gt: 'greater than',
  gte: 'at least',
};

const COMPARISON_MARK: Record<PerformanceTargetComparison, string> = {
  lt: '<',
  lte: '≤',
  gt: '>',
  gte: '≥',
};

function scaleDecimals(step: number, scale: number): number {
  const scaled = step / scale;
  if (!Number.isFinite(scaled) || scaled <= 0 || scaled >= 1) return 0;
  return Math.min(2, Math.ceil(-Math.log10(scaled)));
}

function formatNumber(value: number, step = 1): string {
  if (!Number.isFinite(value)) return '—';
  if (Number.isInteger(value) && step >= 1) return String(value);
  const decimals = step > 0 && step < 1 ? Math.min(4, Math.ceil(-Math.log10(step))) : 1;
  return value.toFixed(decimals);
}

export function formatMetricValue(unit: string, value: number, step = 1): string {
  if (!Number.isFinite(value)) return '—';
  if (unit === 'ms') {
    if (value >= 60_000) return `${Math.round(value / 60_000)} min`;
    if (value >= 1000) return `${(value / 1000).toFixed(Math.max(1, scaleDecimals(step, 1000)))} s`;
    if (value >= 1 && step >= 1) return `${Math.round(value)} ms`;
    if (value === 0) return '0 ms';
    return `${Number(value.toPrecision(3))} ms`;
  }
  if (unit === 'fps') return `${formatNumber(value, step)} fps`;
  if (unit === 'MB' || unit === 'mb') return `${formatNumber(value, step)} MB`;
  if (unit === 'count') return formatNumber(value, step);
  if (unit.length === 0) return formatNumber(value, step);
  return `${formatNumber(value, step)} ${unit}`;
}

export function formatAxisValue(unit: string, value: number, step: number): string {
  return formatMetricValue(unit, value, step);
}

export function formatTargetLine(lane: UiBudgetLane): string {
  const target = lane.target;
  if (!target) return 'no target';
  const unit = target.unit ?? lane.unit;
  return `${COMPARISON_MARK[target.comparison]} ${formatMetricValue(unit, target.value)}`;
}

export function formatTargetSentence(lane: UiBudgetLane): string {
  const target = lane.target;
  if (!target) return 'No Settings target. Edit targets on the Performance tab.';
  const unit = target.unit ?? lane.unit;
  return `${COMPARISON_LABEL[target.comparison]} ${formatMetricValue(unit, target.value)}`;
}

export function latestPoint(lane: UiBudgetLane): UiBudgetPoint | null {
  const pool = [...lane.nightly, ...lane.mr];
  if (pool.length === 0) return null;
  return pool.reduce((latest, point) => (point.t >= latest.t ? point : latest));
}

export function formatLatest(lane: UiBudgetLane): string {
  const point = latestPoint(lane);
  if (!point) return lane.target ? 'no samples yet' : 'no samples';
  return formatMetricValue(lane.unit, point.value);
}

export function emptyLaneReason(lane: UiBudgetLane): string {
  if (lane.status === 'empty') {
    return lane.target
      ? 'The target is listed so a missing measurement stays visible. Nothing has published a sample in this window.'
      : 'No samples and no target.';
  }
  if (lane.status === 'no-baseline') {
    return 'MR samples have no nightly to compare. That is not a regression.';
  }
  return '';
}

export function shortSha(sha: string): string {
  return sha.slice(0, 8);
}
